import { useTranslation } from 'next-i18next';
import { memo, useEffect } from 'react';
import { Box, ButtonV2, Text } from '@bridebook/ui';
import { IconCross } from '@bridebook/ui/src/icons';
import { useMeasure } from '@uidotdev/usehooks';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import {
  hideDownloadAppBannerSessionStorage,
  shouldShowDownloadAppBanner,
} from '@bridebook/web/lib/app/download-app-banner/download-app-banner-storage';
import { selectLayoutProps } from '@bridebook/web/lib/app/selectors';
import { hideDownloadAppBanner, setDownloadAppBannerHeight } from '@bridebook/web/lib/ui/actions';
import { clickedAnalytics } from '@bridebook/web/lib/ui/analytics';
import { getDownloadAppBanner } from '@bridebook/web/lib/ui/selectors';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { AppBannerIcon } from './app-banner-icon';
import { styles } from './download-app-banner.style';
import { StarIcon } from './star-icon';
import { useShouldHideAppBanner } from '@bridebook/web/lib/app/download-app-banner/hooks/use-should-hide-app-banner';

export const LINK_TO_APP_IN_STORE = 'https://bridebook.onelink.me/q51D/54b7dd2';

const DownloadAppBannerBase = () => {
  const { isCordova, isMobile } = useSelector(selectLayoutProps);
  const downloadAppBanner = useSelector(getDownloadAppBanner);
  const dispatch = useDispatch();
  const shouldHideAppBanner = useShouldHideAppBanner();
  const [containerRef, { height }] = useMeasure();

  useEffect(() => {
    if (typeof height === 'number') {
      dispatch(setDownloadAppBannerHeight({ height }));
    }
  }, [height, dispatch]);

  const appBannerCombinedCondition =
    downloadAppBanner &&
    !shouldHideAppBanner &&
    !isCordova &&
    isMobile &&
    shouldShowDownloadAppBanner();

  return (
    <div className="sticky top-0 right-0 left-0 z-[1000]" ref={containerRef}>
      {appBannerCombinedCondition && <DownloadAppBannerComponent />}
    </div>
  );
};

export const DownloadAppBanner = memo(DownloadAppBannerBase);

const DownloadAppBannerComponent = () => {
  const { t } = useTranslation('mobileApp');
  const dispatch = useDispatch();

  const triggerAnalytics = ({ cta }: { cta: string }) => {
    dispatch(
      clickedAnalytics({
        category: 'prompt',
        cta,
        clickedSection: 'downloadAppBanner',
      }),
    );
  };

  const handleHideBannerClick = () => {
    hideDownloadAppBannerSessionStorage();
    dispatch(hideDownloadAppBanner());
    triggerAnalytics({ cta: 'closeButton' });
  };

  return (
    <div className="flex flex-row items-center justify-center gap-2 border-b border-space-15 bg-indigo-crush p-2 lg:hidden">
      <Box style={styles.closeButton} as="button" onClick={handleHideBannerClick}>
        <IconCross width={14} color="white" />
      </Box>
      <AppBannerIcon />
      <Box style={styles.textWrapper}>
        <Text color="white" style={styles.headline} textAlign="start">
          {t('downloadAppBanner.headline')}
        </Text>
        <Box style={styles.bottomLine}>
          <Box style={styles.stars}>
            <StarIcon />
            <StarIcon />
            <StarIcon />
            <StarIcon />
            <StarIcon />
          </Box>
          <Text color="white" style={styles.rating}>
            (9.3K)
          </Text>
        </Box>
      </Box>
      <Box style={styles.buttonWrapper}>
        <Link href={LINK_TO_APP_IN_STORE} onClick={() => triggerAnalytics({ cta: 'Use App' })}>
          <ButtonV2 variant="secondary" size="small" styleWrapper={styles.buttonStyleWrapper}>
            {t('downloadAppBanner.button')}
          </ButtonV2>
        </Link>
      </Box>
    </div>
  );
};
